import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { privacyNoticeApi, processingApi, organizationApi, openPdfExport } from '../services/api';
import {
  PrivacyNotice,
  PrivacyNoticeVersion,
  PrivacyNoticeSection,
  PrivacyNoticeSectionType,
  PrivacyNoticeStatus,
  PrivacyNoticeChannel,
  ProcessingActivity,
  Organization,
  Language,
  ProcessingStatus
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  TextArea,
  Input,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Label
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

// ---------------------------------------------------------------------------
// Label mappings
// ---------------------------------------------------------------------------

const channelLabels: Record<PrivacyNoticeChannel, string> = {
  [PrivacyNoticeChannel.WEBSITE]: 'Website',
  [PrivacyNoticeChannel.APP]: 'App',
  [PrivacyNoticeChannel.APPLICATION_FORM]: 'Bewerbungsformular',
  [PrivacyNoticeChannel.EMPLOYMENT]: 'Beschäftigung',
  [PrivacyNoticeChannel.NEWSLETTER]: 'Newsletter',
  [PrivacyNoticeChannel.CUSTOM]: 'Benutzerdefiniert'
};

const languageLabels: Record<Language, string> = {
  [Language.DE]: 'Deutsch',
  [Language.EN]: 'Englisch'
};

const sectionTypeLabels: Record<PrivacyNoticeSectionType, string> = {
  [PrivacyNoticeSectionType.CONTROLLER]: 'Verantwortlicher',
  [PrivacyNoticeSectionType.DPO]: 'Datenschutzbeauftragter',
  [PrivacyNoticeSectionType.PURPOSE]: 'Zweck & Rechtsgrundlage',
  [PrivacyNoticeSectionType.RECIPIENTS]: 'Empfänger',
  [PrivacyNoticeSectionType.THIRD_COUNTRY]: 'Drittlandtransfer',
  [PrivacyNoticeSectionType.RETENTION]: 'Speicherdauer',
  [PrivacyNoticeSectionType.RIGHTS]: 'Betroffenenrechte',
  [PrivacyNoticeSectionType.COMPLAINT]: 'Beschwerderecht',
  [PrivacyNoticeSectionType.OBLIGATION]: 'Bereitstellungspflicht',
  [PrivacyNoticeSectionType.SOURCE]: 'Datenherkunft',
  [PrivacyNoticeSectionType.CUSTOM]: 'Benutzerdefiniert'
};

const statusConfig: Record<PrivacyNoticeStatus, { label: string; state: string }> = {
  [PrivacyNoticeStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [PrivacyNoticeStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [PrivacyNoticeStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [PrivacyNoticeStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const processingStatusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

// ---------------------------------------------------------------------------
// Section editor state per section id
// ---------------------------------------------------------------------------

interface SectionEditState {
  short_text: string;
  full_text: string;
  saving: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PrivacyNoticeDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete, canApprove } = useUserRole();

  const [notice, setNotice] = useState<PrivacyNotice | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Versions tab
  const [versions, setVersions] = useState<PrivacyNoticeVersion[]>([]);
  const [versionsLoaded, setVersionsLoaded] = useState(false);
  const [newVersionNotes, setNewVersionNotes] = useState('');
  const [creatingVersion, setCreatingVersion] = useState(false);

  // Verarbeitungen tab
  const [linkedActivities, setLinkedActivities] = useState<ProcessingActivity[]>([]);
  const [allActivities, setAllActivities] = useState<ProcessingActivity[]>([]);
  const [showActivityLinkPanel, setShowActivityLinkPanel] = useState(false);

  // Section editing state (keyed by section id)
  const [sectionEdits, setSectionEdits] = useState<Record<number, SectionEditState>>({});
  const [regeneratingAll, setRegeneratingAll] = useState(false);

  // Copy-to-clipboard feedback
  const [shortCopied, setShortCopied] = useState(false);
  const [fullCopied, setFullCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const noticeId = parseInt(id);
        const [noticeRes, allActivitiesRes] = await Promise.all([
          privacyNoticeApi.getById(noticeId, org.id),
          processingApi.getByOrganization(org.id)
        ]);
        if (noticeRes.success && noticeRes.data) {
          const noticeData = noticeRes.data;
          setNotice(noticeData);
          setLinkedActivities(noticeData.processing_activities ?? []);
          // Init section edit states from current version sections
          if (noticeData.current_version?.sections) {
            const edits: Record<number, SectionEditState> = {};
            for (const section of noticeData.current_version.sections) {
              edits[section.id] = {
                short_text: section.short_text,
                full_text: section.full_text,
                saving: false
              };
            }
            setSectionEdits(edits);
          }
        } else {
          navigate('/privacy-notices');
        }
        if (allActivitiesRes.success && allActivitiesRes.data) {
          setAllActivities(allActivitiesRes.data);
        }
      }
    }
    setLoading(false);
  };

  const loadVersions = async () => {
    if (!notice || !currentOrg || versionsLoaded) return;
    const res = await privacyNoticeApi.getVersions(notice.id, currentOrg.id);
    if (res.success && res.data) {
      setVersions(res.data);
      setVersionsLoaded(true);
    }
  };

  const handleEdit = () => navigate(`/privacy-notices/${id}/edit`);

  const handleDelete = async () => {
    if (!notice || !currentOrg) return;
    const response = await privacyNoticeApi.delete(notice.id, currentOrg.id);
    if (response.success) navigate('/privacy-notices');
  };

  const handleStatusChange = async (newStatus: PrivacyNoticeStatus) => {
    if (!notice || !currentOrg) return;
    const response = await privacyNoticeApi.updateStatus(notice.id, currentOrg.id, newStatus);
    if (response.success && response.data) setNotice(response.data);
  };

  const handleRegenerate = async () => {
    if (!notice || !currentOrg) return;
    setRegeneratingAll(true);
    const response = await privacyNoticeApi.regenerate(notice.id, currentOrg.id);
    if (response.success && response.data) {
      setNotice(response.data);
      // Re-init section edits
      if (response.data.current_version?.sections) {
        const edits: Record<number, SectionEditState> = {};
        for (const section of response.data.current_version.sections) {
          edits[section.id] = {
            short_text: section.short_text,
            full_text: section.full_text,
            saving: false
          };
        }
        setSectionEdits(edits);
      }
      setVersionsLoaded(false); // invalidate cached versions
    }
    setRegeneratingAll(false);
  };

  const handleSaveSection = async (section: PrivacyNoticeSection) => {
    if (!notice || !currentOrg) return;
    const editState = sectionEdits[section.id];
    if (!editState) return;
    setSectionEdits(prev => ({ ...prev, [section.id]: { ...prev[section.id], saving: true } }));
    const response = await privacyNoticeApi.updateSection(notice.id, section.id, currentOrg.id, {
      short_text: editState.short_text,
      full_text: editState.full_text
    });
    if (response.success && response.data) {
      // Update the section in notice state
      setNotice(prev => {
        if (!prev?.current_version?.sections) return prev;
        return {
          ...prev,
          current_version: {
            ...prev.current_version,
            sections: prev.current_version.sections.map(s =>
              s.id === section.id ? response.data! : s
            )
          }
        };
      });
    }
    setSectionEdits(prev => ({ ...prev, [section.id]: { ...prev[section.id], saving: false } }));
  };

  const handleRegenerateSection = async (_section: PrivacyNoticeSection) => {
    // Regenerating a single section re-runs the full regeneration and updates in-place
    await handleRegenerate();
  };

  const handleCreateVersion = async () => {
    if (!notice || !currentOrg) return;
    setCreatingVersion(true);
    const response = await privacyNoticeApi.createVersion(notice.id, currentOrg.id, newVersionNotes || undefined);
    if (response.success && response.data) {
      setVersions(prev => [response.data!, ...prev]);
      setNewVersionNotes('');
      // Reload notice to get updated current_version
      const noticeRes = await privacyNoticeApi.getById(notice.id, currentOrg.id);
      if (noticeRes.success && noticeRes.data) {
        setNotice(noticeRes.data);
      }
    }
    setCreatingVersion(false);
  };

  const handleLinkActivity = async (paId: number) => {
    if (!notice || !currentOrg) return;
    const response = await privacyNoticeApi.linkActivity(notice.id, paId, currentOrg.id);
    if (response.success) {
      const linked = allActivities.find(a => a.id === paId);
      if (linked) setLinkedActivities(prev => [...prev, linked]);
    }
  };

  const handleUnlinkActivity = async (paId: number) => {
    if (!notice || !currentOrg) return;
    const response = await privacyNoticeApi.unlinkActivity(notice.id, paId, currentOrg.id);
    if (response.success) {
      setLinkedActivities(prev => prev.filter(a => a.id !== paId));
    }
  };

  const copyToClipboard = async (text: string, type: 'short' | 'full') => {
    await navigator.clipboard.writeText(text);
    if (type === 'short') {
      setShortCopied(true);
      setTimeout(() => setShortCopied(false), 2000);
    } else {
      setFullCopied(true);
      setTimeout(() => setFullCopied(false), 2000);
    }
  };

  const unlinkedActivities = allActivities.filter(
    a => !linkedActivities.some(l => l.id === a.id)
  );

  const sections = notice?.current_version?.sections ?? [];

  // ---------------------------------------------------------------------------
  // Tab renderers
  // ---------------------------------------------------------------------------

  const renderKurzinfoTab = () => {
    const allShortText = sections
      .map(s => `${s.heading}\n\n${s.short_text}`)
      .join('\n\n---\n\n');

    return (
      <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {sections.length === 0 ? (
          <Card>
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
              <Icon name="document-text" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                Keine Bausteine vorhanden. Bitte verknüpfen Sie zunächst Verarbeitungstätigkeiten.
              </Text>
            </div>
          </Card>
        ) : (
          <>
            <FlexBox justifyContent="End">
              <Button
                icon={shortCopied ? 'accept' : 'copy'}
                design={shortCopied ? 'Positive' : 'Default'}
                onClick={() => copyToClipboard(allShortText, 'short')}
              >
                {shortCopied ? 'Kopiert!' : 'Alles kopieren'}
              </Button>
            </FlexBox>
            {sections.map(section => (
              <Card
                key={section.id}
                header={
                  <CardHeader
                    titleText={section.heading}
                    avatar={<Icon name="hint" />}
                  />
                }
              >
                <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
                  <Text style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    {section.short_text}
                  </Text>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    );
  };

  const renderVolltextTab = () => {
    const allFullText = sections
      .map(s => `${s.heading}\n\n${s.full_text}`)
      .join('\n\n---\n\n');

    return (
      <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {sections.length === 0 ? (
          <Card>
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
              <Icon name="document-text" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                Keine Bausteine vorhanden. Bitte verknüpfen Sie zunächst Verarbeitungstätigkeiten.
              </Text>
            </div>
          </Card>
        ) : (
          <>
            <FlexBox justifyContent="End">
              <Button
                icon={fullCopied ? 'accept' : 'copy'}
                design={fullCopied ? 'Positive' : 'Default'}
                onClick={() => copyToClipboard(allFullText, 'full')}
              >
                {fullCopied ? 'Kopiert!' : 'Alles kopieren'}
              </Button>
            </FlexBox>
            {sections.map(section => (
              <Card
                key={section.id}
                header={
                  <CardHeader
                    titleText={section.heading}
                    avatar={<Icon name="document-text" />}
                  />
                }
              >
                <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
                  <Text style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    {section.full_text}
                  </Text>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    );
  };

  const renderBausteineTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {sections.length === 0 ? (
        <Card>
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
            <Icon name="puzzle" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
              Keine Bausteine vorhanden. Bitte verknüpfen Sie zunächst Verarbeitungstätigkeiten.
            </Text>
          </div>
        </Card>
      ) : (
        <>
          <FlexBox justifyContent="End">
            <Button
              icon="synchronize"
              design="Default"
              onClick={handleRegenerate}
              disabled={regeneratingAll}
            >
              {regeneratingAll ? 'Regeneriere...' : 'Alle automatischen Bausteine regenerieren'}
            </Button>
          </FlexBox>

          {sections.map(section => {
            const editState = sectionEdits[section.id] ?? {
              short_text: section.short_text,
              full_text: section.full_text,
              saving: false
            };

            return (
              <Card
                key={section.id}
                header={
                  <CardHeader
                    titleText={section.heading}
                    avatar={<Icon name="puzzle" />}
                    action={
                      <FlexBox style={{ gap: '0.5rem' }} alignItems="Center">
                        <ObjectStatus
                          state={section.is_auto_generated ? 'Information' : 'None'}
                          showDefaultIcon
                        >
                          {section.is_auto_generated ? 'Automatisch' : 'Manuell'}
                        </ObjectStatus>
                        <ObjectStatus state="None">
                          {sectionTypeLabels[section.section_type] ?? section.section_type}
                        </ObjectStatus>
                        {section.is_auto_generated && (
                          <Button
                            design="Transparent"
                            icon="synchronize"
                            onClick={() => handleRegenerateSection(section)}
                            disabled={regeneratingAll}
                          >
                            Regenerieren
                          </Button>
                        )}
                        <Button
                          design="Emphasized"
                          icon="save"
                          onClick={() => handleSaveSection(section)}
                          disabled={editState.saving}
                        >
                          {editState.saving ? 'Speichert...' : 'Speichern'}
                        </Button>
                      </FlexBox>
                    }
                  />
                }
              >
                <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <Label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                      Kurztext (Layer 1)
                    </Label>
                    <TextArea
                      value={editState.short_text}
                      rows={4}
                      style={{ width: '100%' }}
                      onInput={(e: any) => {
                        setSectionEdits(prev => ({
                          ...prev,
                          [section.id]: {
                            ...prev[section.id],
                            short_text: e.target.value
                          }
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <Label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                      Volltext (Layer 2)
                    </Label>
                    <TextArea
                      value={editState.full_text}
                      rows={6}
                      style={{ width: '100%' }}
                      onInput={(e: any) => {
                        setSectionEdits(prev => ({
                          ...prev,
                          [section.id]: {
                            ...prev[section.id],
                            full_text: e.target.value
                          }
                        }));
                      }}
                    />
                  </div>
                </div>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );

  const renderVersionenTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Create new version */}
      <Card header={<CardHeader titleText="Neue Version erstellen" avatar={<Icon name="add" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
              Notizen (optional)
            </Label>
            <Input
              value={newVersionNotes}
              placeholder="z. B. Anpassung nach Rechtsänderung..."
              style={{ width: '100%' }}
              onInput={(e: any) => setNewVersionNotes(e.target.value)}
            />
          </div>
          <FlexBox justifyContent="End">
            <Button
              design="Emphasized"
              icon="add"
              onClick={handleCreateVersion}
              disabled={creatingVersion}
            >
              {creatingVersion ? 'Erstelle...' : 'Neue Version erstellen'}
            </Button>
          </FlexBox>
        </div>
      </Card>

      {/* Version list */}
      {versions.length === 0 && versionsLoaded ? (
        <Card>
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
            <Icon name="history" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
              Noch keine Versionen vorhanden.
            </Text>
          </div>
        </Card>
      ) : (
        versions.map(version => (
          <Card key={version.id}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
              <FlexBox justifyContent="SpaceBetween" alignItems="Start" style={{ gap: '1rem' }}>
                <div>
                  <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.375rem' }}>
                    <Text style={{ fontWeight: '600', fontSize: '1rem' }}>Version {version.version_number}</Text>
                    {notice?.current_version_id === version.id && (
                      <ObjectStatus state="Success" showDefaultIcon>Aktuell</ObjectStatus>
                    )}
                  </FlexBox>
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    Erstellt: {new Date(version.created_at).toLocaleString('de-DE')}
                  </Text>
                  {version.valid_from && (
                    <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.25rem' }}>
                      Gültig von: {new Date(version.valid_from).toLocaleDateString('de-DE')}
                      {version.valid_until ? ` bis ${new Date(version.valid_until).toLocaleDateString('de-DE')}` : ''}
                    </Text>
                  )}
                  {version.notes && (
                    <Text style={{ fontSize: '0.8125rem', marginTop: '0.375rem', fontStyle: 'italic' }}>
                      {version.notes}
                    </Text>
                  )}
                </div>
              </FlexBox>
            </div>
          </Card>
        ))
      )}
    </div>
  );

  const renderVerarbeitungenTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <FlexBox justifyContent="End">
        <Button
          icon="chain-link"
          onClick={() => setShowActivityLinkPanel(true)}
          disabled={unlinkedActivities.length === 0}
        >
          Verknüpfung hinzufügen
        </Button>
      </FlexBox>

      {linkedActivities.length === 0 ? (
        <Card>
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
            <Icon name="documents" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
              Keine verknüpften Verarbeitungstätigkeiten
            </Text>
          </div>
        </Card>
      ) : (
        <Card>
          <Table
            columns={
              <>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Name</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Abteilung</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Status</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Aktionen</Text></TableColumn>
              </>
            }
          >
            {linkedActivities.map(activity => (
              <TableRow
                key={activity.id}
                type="Active"
                onClick={() => navigate(`/processing/${activity.id}`)}
              >
                <TableCell>
                  <Text style={{ fontWeight: '500', color: 'var(--sapLinkColor)' }}>{activity.name}</Text>
                </TableCell>
                <TableCell>
                  <Text>{activity.department}</Text>
                </TableCell>
                <TableCell>
                  <ObjectStatus state={processingStatusConfig[activity.status].state as any} showDefaultIcon>
                    {processingStatusConfig[activity.status].label}
                  </ObjectStatus>
                </TableCell>
                <TableCell>
                  <Button
                    design="Transparent"
                    icon="chain-link"
                    style={{ color: 'var(--sapNegativeColor)' }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleUnlinkActivity(activity.id);
                    }}
                  >
                    Entfernen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Card>
      )}

      {/* Inline link panel */}
      {showActivityLinkPanel && (
        <Card header={<CardHeader titleText="Verarbeitungstätigkeit verknüpfen" avatar={<Icon name="chain-link" />} />}>
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            {unlinkedActivities.length === 0 ? (
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                Alle Verarbeitungstätigkeiten sind bereits verknüpft.
              </Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {unlinkedActivities.map(activity => (
                  <FlexBox
                    key={activity.id}
                    alignItems="Center"
                    justifyContent="SpaceBetween"
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                      background: 'var(--sapBaseColor)'
                    }}
                  >
                    <div>
                      <Text style={{ fontWeight: '500', display: 'block' }}>{activity.name}</Text>
                      <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                        {activity.department} · {processingStatusConfig[activity.status].label}
                      </Text>
                    </div>
                    <Button
                      icon="add"
                      design="Emphasized"
                      onClick={() => handleLinkActivity(activity.id)}
                    >
                      Verknüpfen
                    </Button>
                  </FlexBox>
                ))}
              </div>
            )}
            <FlexBox justifyContent="End" style={{ marginTop: '1rem' }}>
              <Button design="Transparent" onClick={() => setShowActivityLinkPanel(false)}>Schließen</Button>
            </FlexBox>
          </div>
        </Card>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Loading / not found guard
  // ---------------------------------------------------------------------------

  if (loading || !notice) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="page-layout--detail">
      {/* Back link */}
      <Button
        icon="nav-back"
        design="Transparent"
        onClick={() => navigate('/privacy-notices')}
        style={{ marginBottom: '0.75rem' }}
      >
        Zurück zur Übersicht
      </Button>

      {/* Outdated warning strip */}
      {notice.is_outdated && (
        <div style={{ marginBottom: '0.75rem' }}>
          <MessageStrip design="Warning" hideCloseButton>
            Quellaten haben sich geändert. Der Datenschutzhinweis ist möglicherweise veraltet. Bitte regenerieren Sie den Hinweis.
          </MessageStrip>
        </div>
      )}

      {/* Object Header Card */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <FlexBox justifyContent="SpaceBetween" alignItems="Start" wrap="Wrap" style={{ gap: '1rem' }}>
            <div style={{ flex: '1 1 auto' }}>
              <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.5rem' }} wrap="Wrap">
                <Title level="H3" style={{ margin: 0 }}>{notice.title}</Title>
                <ObjectStatus state={statusConfig[notice.status].state as any} showDefaultIcon>
                  {statusConfig[notice.status].label}
                </ObjectStatus>
                {notice.is_outdated && (
                  <ObjectStatus state="Warning" showDefaultIcon>Veraltet</ObjectStatus>
                )}
              </FlexBox>

              {/* Key attributes */}
              <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginTop: '0.75rem' }}>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="world" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {channelLabels[notice.channel] ?? notice.channel}
                  </Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="translate" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {languageLabels[notice.language as Language] ?? notice.language}
                  </Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="tag" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    v{notice.current_version?.version_number ?? 1}
                  </Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="calendar" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {new Date(notice.updated_at).toLocaleDateString('de-DE')}
                  </Text>
                </FlexBox>
              </FlexBox>
            </div>

            {/* Action buttons */}
            <FlexBox style={{ gap: '0.5rem' }} wrap="Wrap">
              <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('privacy-notices/' + id, currentOrg.id)}>PDF</Button>
              {canEdit && notice.status !== PrivacyNoticeStatus.ARCHIVED && (
                <Button icon="edit" onClick={handleEdit}>Bearbeiten</Button>
              )}
              {canEdit && notice.is_outdated && (
                <Button
                  design="Attention"
                  icon="synchronize"
                  onClick={handleRegenerate}
                  disabled={regeneratingAll}
                >
                  {regeneratingAll ? 'Aktualisiere...' : 'Aktualisieren'}
                </Button>
              )}
              {canEdit && notice.status === PrivacyNoticeStatus.DRAFT && (
                <Button
                  design="Emphasized"
                  icon="paper-plane"
                  onClick={() => handleStatusChange(PrivacyNoticeStatus.IN_REVIEW)}
                >
                  Zur Prüfung einreichen
                </Button>
              )}
              {notice.status === PrivacyNoticeStatus.IN_REVIEW && (
                <>
                  {canEdit && (
                    <Button
                      design="Transparent"
                      icon="undo"
                      onClick={() => handleStatusChange(PrivacyNoticeStatus.DRAFT)}
                    >
                      Zurück zu Entwurf
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      design="Emphasized"
                      icon="accept"
                      onClick={() => handleStatusChange(PrivacyNoticeStatus.APPROVED)}
                    >
                      Freigeben
                    </Button>
                  )}
                </>
              )}
              {notice.status === PrivacyNoticeStatus.APPROVED && (
                <>
                  {canEdit && (
                    <Button
                      design="Transparent"
                      icon="undo"
                      onClick={() => handleStatusChange(PrivacyNoticeStatus.DRAFT)}
                    >
                      Zurück zu Entwurf
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      design="Attention"
                      icon="archive"
                      onClick={() => handleStatusChange(PrivacyNoticeStatus.ARCHIVED)}
                    >
                      Archivieren
                    </Button>
                  )}
                </>
              )}
              {canEdit && notice.status === PrivacyNoticeStatus.ARCHIVED && (
                <Button
                  design="Emphasized"
                  icon="undo"
                  onClick={() => handleStatusChange(PrivacyNoticeStatus.DRAFT)}
                >
                  Reaktivieren
                </Button>
              )}
              {canDelete && (
                <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>
                  Löschen
                </Button>
              )}
            </FlexBox>
          </FlexBox>
        </div>
      </Card>

      {/* Tabs */}
      <TabContainer
        onTabSelect={(e: any) => {
          // Lazy-load versions when that tab is first selected
          const tabText = e.detail?.tab?.getAttribute?.('text') ?? '';
          if (tabText === 'Versionen' && !versionsLoaded) {
            loadVersions();
          }
        }}
      >
        <Tab text="Kurzinfo" icon="hint" selected>
          {renderKurzinfoTab()}
        </Tab>
        <Tab text="Volltext" icon="document-text">
          {renderVolltextTab()}
        </Tab>
        <Tab text="Bausteine" icon="puzzle">
          {renderBausteineTab()}
        </Tab>
        <Tab text="Versionen" icon="history">
          {renderVersionenTab()}
        </Tab>
        <Tab text="Verarbeitungen" icon="documents">
          {renderVerarbeitungenTab()}
        </Tab>
      </TabContainer>

      {/* Delete confirmation dialog */}
      {showDeleteDialog && (
        <MessageBox
          open={showDeleteDialog}
          onClose={(e: any) => {
            setShowDeleteDialog(false);
            if (e.detail.action === 'OK') handleDelete();
          }}
          type="Confirm"
          titleText="Datenschutzhinweis löschen?"
        >
          Möchten Sie diesen Datenschutzhinweis wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </MessageBox>
      )}
    </div>
  );
}
